/**
 * Persisted state that belongs to one account and outlives a reload.
 *
 * Switching accounts reloads the page, which drops everything held in memory but
 * not what sits in local or session storage. The entries listed here are keyed by
 * per-tenant ids (form ids, requester ids, planning ids), so rehydrating them
 * under the new account applies the previous account's ids to a different
 * tenant's data: filters that match nothing, a wizard draft belonging elsewhere.
 *
 * Only state whose meaning depends on the account belongs here. Device
 * preferences (sidebar, search mode) and anything already keyed by the
 * per-account user id survive the switch on purpose.
 */
const LOCAL_STORAGE_PREFIXES = ['datascope.signature.filters.'];

const SESSION_STORAGE_PREFIXES = ['plannings.editPublished.snapshot.', 'plannings.wizard.draft'];

function purge(storage: Storage, prefixes: string[]): void {
  try {
    const doomed: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key !== null && prefixes.some((prefix) => key.startsWith(prefix))) doomed.push(key);
    }
    doomed.forEach((key) => storage.removeItem(key));
  } catch {
    // Private mode or a quota error: the switch has to go through anyway.
  }
}

export function clearAccountScopedStorage(): void {
  if (typeof window === 'undefined') return;
  purge(window.localStorage, LOCAL_STORAGE_PREFIXES);
  purge(window.sessionStorage, SESSION_STORAGE_PREFIXES);
}
